<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  useKnowledgeBase,
  type KnowledgeBaseEntry,
} from '~/composables/useKnowledgeBase'
import Markdown from 'vue3-markdown-it'

const props = defineProps<{
  entry: KnowledgeBaseEntry
}>()

const emit = defineEmits<{
  (e: 'edit', entry: KnowledgeBaseEntry): void
  (e: 'delete', id: string): void
}>()

const knowledgeBase = useKnowledgeBase()
const confirmingDelete = ref(false)
const hasEditPermissions = computed(
  () => knowledgeBase.hasEditPermissions.value,
)

function confirmDelete() {
  confirmingDelete.value = true
}

function cancelDelete() {
  confirmingDelete.value = false
}

function deleteEntry() {
  emit('delete', props.entry.id)
  confirmingDelete.value = false
}

function editEntry() {
  emit('edit', props.entry)
}

// Format the date
function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Apply custom styling to the content after it's rendered
onMounted(() => {
  // This will run after the component is mounted and the markdown is rendered
  // We can use this to apply additional styling or processing to the rendered markdown
  setTimeout(() => {
    // Process image captions - find images and add captions based on the alt text
    const images = document.querySelectorAll('.kb-entry img')
    images.forEach((img) => {
      // Check if the image already has a caption
      const parent = img.parentElement
      if (
        parent &&
        parent.tagName === 'P' &&
        !parent.classList.contains('img-with-caption')
      ) {
        const altText = img.getAttribute('alt')
        if (altText) {
          // Add caption after the image
          const caption = document.createElement('span')
          caption.className = 'image-caption'
          caption.textContent = altText

          // Add class to parent to mark it as processed
          parent.classList.add('img-with-caption')

          // Insert caption after the image
          img.after(caption)
        }
      }
    })
  }, 100)
})
</script>

<template>
  <div class="kb-entry">
    <div v-if="confirmingDelete" class="bg-red-50 p-4 mb-4 rounded-md">
      <h3 class="text-red-800 font-medium">Confirm Deletion</h3>
      <p class="text-red-700 mt-1">
        Are you sure you want to delete this entry? This action cannot be
        undone.
      </p>
      <div class="flex space-x-2 mt-3">
        <button
          class="px-3 py-1.5 border border-red-600 text-red-600 rounded-md text-sm font-medium hover:bg-red-50"
          type="button"
          @click="deleteEntry"
        >
          Yes, Delete
        </button>
        <button
          class="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
          type="button"
          @click="cancelDelete"
        >
          Cancel
        </button>
      </div>
    </div>

    <div class="mb-4">
      <h1 class="text-xl font-bold text-gray-900 mb-2">
        {{ entry.title }}
      </h1>
      <div class="flex justify-between text-sm text-gray-500">
        <div>By {{ entry.createdBy }}</div>
        <div>Updated {{ formatDate(entry.updatedAt) }}</div>
      </div>

      <div
        v-if="hasEditPermissions && !confirmingDelete"
        class="flex mt-2 space-x-2"
      >
        <button
          class="flex items-center text-sm text-primary-600 hover:text-primary-800"
          type="button"
          @click="editEntry"
        >
          <div class="i-heroicons-solid-pencil h-4 w-4 mr-1" />
          Edit
        </button>
        <button
          class="flex items-center text-sm text-red-600 hover:text-red-800"
          type="button"
          @click="confirmDelete"
        >
          <div class="i-heroicons-solid-trash h-4 w-4 mr-1" />
          Delete
        </button>
      </div>
    </div>

    <div class="prose prose-sm max-w-none overflow-auto">
      <Markdown
        :source="entry.content"
        :html="true"
        :linkify="true"
        :typographer="true"
        :breaks="true"
        class="knowledge-base-content"
      />
    </div>
  </div>
</template>

<style scoped>
.kb-entry :deep(h1) {
  @apply text-xl font-bold mt-6 mb-2 text-gray-900;
}

.kb-entry :deep(h2) {
  @apply text-lg font-semibold mt-5 mb-2 text-gray-800;
}

.kb-entry :deep(h3) {
  @apply text-base font-medium mt-4 mb-2 text-gray-800;
}

.kb-entry :deep(p) {
  @apply mb-3 text-gray-700;
}

.kb-entry :deep(ul) {
  @apply list-disc pl-6 mb-3;
}

.kb-entry :deep(ol) {
  @apply list-decimal pl-6 mb-3;
}

.kb-entry :deep(li) {
  @apply mb-1;
}

.kb-entry :deep(strong) {
  @apply font-semibold;
}

.kb-entry :deep(em) {
  @apply italic;
}

.kb-entry :deep(a) {
  @apply text-primary-600 hover:text-primary-800 hover:underline;
}

.kb-entry :deep(code) {
  @apply bg-gray-100 text-red-500 px-1.5 py-0.5 rounded font-mono text-sm;
}

.kb-entry :deep(pre) {
  @apply bg-gray-100 p-3 rounded-md overflow-auto my-3;
}

.kb-entry :deep(blockquote) {
  @apply border-l-4 border-gray-200 pl-4 italic text-gray-600 my-3;
}

.kb-entry :deep(hr) {
  @apply my-6 border-t border-gray-200;
}

.kb-entry :deep(table) {
  @apply min-w-full divide-y divide-gray-200 my-3;
}

.kb-entry :deep(th) {
  @apply px-3 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.kb-entry :deep(td) {
  @apply px-3 py-2 whitespace-nowrap text-sm text-gray-500;
}

/* Enhanced styles for images */
.kb-entry :deep(img) {
  @apply max-w-full h-auto rounded-lg border border-gray-200 shadow-sm my-4;
}

.kb-entry :deep(.img-with-caption) {
  @apply flex flex-col items-center text-center;
}

.kb-entry :deep(.image-caption) {
  @apply block text-center text-sm text-gray-500 mt-1 mb-4;
}

/* Add additional styles for Tango.ai footers */
.kb-entry :deep(p:has(a[href*='tango.ai'])) {
  @apply text-center text-xs text-gray-400 mt-8 pt-4 border-t border-gray-200;
}
</style>
